<template>
  <v-row v-if="shouldShow" class="mt-2">
    <v-col cols="auto ml-3">
      <v-img contain width="32" max-height="56" :src="$asset('img/icons/payment.svg')" />
    </v-col>
    <v-col class="ml-2">
      <template v-if="isB2b && pricePartlyCovered">
        {{
          $t('questionPriceInfo.b2bDiscount', {
            price: questionPrice,
            currency: currencyLabel,
            discountPercent: discountValue,
            partnerName,
            hours: responseHours
          })
        }}
      </template>
      <template v-else-if="isB2b">
        {{
          $t('questionPriceInfo.b2b', {
            price: questionPrice,
            currency: currencyLabel,
            hours: responseHours
          })
        }}
      </template>
      <template v-else>
        <i18n path="questionPriceInfo.b2c" tag="span">
          <template #price>{{ questionPrice }}</template>
          <template #currency>{{ currencyLabel }}</template>
          <template #hours>{{ responseHours }}</template>
          <template #termsLink>
            <a
              v-if="!isMobileApp"
              :href="$localePath('termsAndConditions')"
              target="_blank"
              class="link--inline black--text"
            >
              {{ $t('questionPriceInfo.b2cLink') }}
            </a>
            <router-link
              v-else
              :to="$localePath('termsAndConditions')"
              class="link--inline black--text"
            >
              {{ $t('questionPriceInfo.b2cLink') }}
            </router-link>
          </template>
        </i18n>
      </template>
    </v-col>
  </v-row>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance } from 'vue';
import {
  getQuestionDefaultPrice,
  getQuestionCurrencyLabel,
  getQuestionResponseHours
} from '@/feature/questions/helpers/questionPricingHelpers';

const instance = getCurrentInstance()!.proxy;
const store = instance.$store;
const auth = instance.$auth;
const locale = instance.$i18n.locale;
const region = instance.$domainRegion;

const isB2b = computed(() => !!auth?.user?.benefit?.partnerUrl && !!auth?.user?.benefit?.active);

const partnerData = computed(() => store.getters['profile/dashboard/partnerData'] || {});

const currentMutation = computed(() => {
  const mutations = partnerData.value?.mutations;
  if (!mutations) {
    return {};
  }
  return (
    mutations.find(
      (mutation: { language: string }) => mutation.language === auth?.user?.language
    ) ?? {}
  );
});

const discountValue = computed(() => currentMutation.value?.discountValue ?? 0);
const partnerName = computed(() => partnerData.value?.name ?? '');

const pricePartlyCovered = computed(
  () =>
    discountValue.value > 0 &&
    discountValue.value < 100 &&
    (currentMutation.value?.questionPrice ?? 0) > 0
);

const defaultPrice = computed(() => getQuestionDefaultPrice(region));
const currencyLabel = computed(() => getQuestionCurrencyLabel(region));

const questionPrice = computed(() => {
  if (isB2b.value && currentMutation.value?.questionPrice !== null) {
    return currentMutation.value.questionPrice;
  }
  return defaultPrice.value;
});

const isMobileApp = computed(() => !!store.state.general.isMobileApp);
const shouldShow = computed(() => questionPrice.value > 0);

const responseHours = computed(() =>
  getQuestionResponseHours({ locale, isB2bActive: isB2b.value })
);
</script>
